import React, { useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';

import {
	joinWorkshopWaitlist,
	groupSeatsRemaining,
	ageRangeError,
	formatSessionOption,
} from '../../utils/workshopService';
import { isValidEmail, friendlyServerError } from '../../utils/formValidation';

const initialForm = {
	parentName: '',
	parentEmail: '',
	parentPhone: '',
	studentName: '',
	studentAge: '',
	note: '',
};

// Shown in place of the registration form when a workshop (or all of its
// sessions) is full. Captures just enough to reach the family when a seat
// opens — no payment, no seat hold. Restyled to the .co- look (Surface 3);
// logic unchanged. Rendered inside RegistrationForm's `.cyv .co` wrapper.
const WaitlistForm = ({ workshop }) => {
	const groups = useMemo(() => workshop.groups || [], [workshop]);
	const requiresGroupSelection = !!workshop.requiresGroupSelection;

	const isClosed = workshop.status === 'closed';
	// A closed workshop can waitlist ANY session; a full-but-open one only
	// waitlists its full sessions (Review 3 #36). Pre-select the single group.
	const selectableGroups = useMemo(
		() => (isClosed ? groups : groups.filter((g) => groupSeatsRemaining(g) <= 0)),
		[groups, isClosed]
	);

	const [groupId, setGroupId] = useState(
		requiresGroupSelection ? '' : groups[0]?.id || ''
	);
	const [form, setForm] = useState(initialForm);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [done, setDone] = useState(false);

	const update = (field) => (e) => {
		setForm((prev) => ({ ...prev, [field]: e.target.value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');

		if (!form.parentName.trim()) {
			setError('Please add a parent or guardian name so we know who to reach.');
			return;
		}
		if (!isValidEmail(form.parentEmail)) {
			setError('Please add an email so we can reach you if a spot opens.');
			return;
		}
		if (!form.parentPhone.trim()) {
			setError('Please add a phone number so we can reach you.');
			return;
		}
		if (!form.studentName.trim()) {
			setError('Please add the student’s name.');
			return;
		}
		if (!form.studentAge.trim()) {
			setError('Please add the student’s age.');
			return;
		}
		if (requiresGroupSelection && !groupId) {
			setError('Please choose which session you’d like to wait for.');
			return;
		}
		const ageErr = ageRangeError(groups.find((g) => g.id === groupId), form.studentAge);
		if (ageErr) {
			setError(ageErr);
			return;
		}

		setSubmitting(true);
		try {
			await joinWorkshopWaitlist({
				workshopId: workshop.slug || workshop.id,
				groupId,
				parent: {
					name: form.parentName.trim(),
					email: form.parentEmail.trim(),
					phone: form.parentPhone.trim(),
				},
				student: {
					name: form.studentName.trim(),
					age: form.studentAge.trim(),
				},
				note: form.note.trim() || null,
			});
			setDone(true);
		} catch (err) {
			console.error('Waitlist signup failed:', err);
			setError(friendlyServerError(err, 'We could not add you to the waitlist. Please try again.'));
			setSubmitting(false);
		}
	};

	if (done) {
		return (
			<div className='co-body' style={{ display: 'block' }}>
				<div className='co-step'>
					<div className='co-notice'>
						<FontAwesomeIcon icon={faCircleCheck} />
						<span>
							You’re on the waitlist! If a spot opens up — or we add another
							session — we’ll reach out to you directly. There’s nothing more you
							need to do.
						</span>
					</div>
				</div>
			</div>
		);
	}

	return (
		<form className='co-body' style={{ display: 'block' }} onSubmit={handleSubmit} noValidate>
			<div className='co-step'>
				<div className='co-notice'>
					<FontAwesomeIcon icon={faCircleInfo} />
					<span>
						{isClosed
							? 'Enrollment for this workshop is closed. Add your family to the waitlist and we’ll contact you directly if a spot opens up or we run it again. You won’t be charged.'
							: 'This workshop is currently full. Add your family to the waitlist and we’ll contact you directly if a spot opens up or we add another session. You won’t be charged.'}
					</span>
				</div>

				{requiresGroupSelection && (
					<div className='co-field full' style={{ marginBottom: '14px' }}>
						<label htmlFor='wl-group'>
							Which session? <span className='req'>*</span>
						</label>
						<select
							id='wl-group'
							className='co-input'
							required
							value={groupId}
							onChange={(e) => setGroupId(e.target.value)}>
							<option value=''>Select a session…</option>
							{selectableGroups.map((g) => (
								<option key={g.id} value={g.id}>
									{formatSessionOption(g)}
								</option>
							))}
						</select>
					</div>
				)}

				<div className='co-grid'>
					<div className='co-field'>
						<label htmlFor='wl-parent-name'>
							Parent / guardian <span className='req'>*</span>
						</label>
						<input
							id='wl-parent-name'
							className='co-input'
							type='text'
							required
							autoComplete='name'
							value={form.parentName}
							onChange={update('parentName')}
						/>
					</div>
					<div className='co-field'>
						<label htmlFor='wl-parent-email'>
							Email <span className='req'>*</span>
						</label>
						<input
							id='wl-parent-email'
							className='co-input'
							type='email'
							required
							autoComplete='email'
							value={form.parentEmail}
							onChange={update('parentEmail')}
						/>
					</div>
					<div className='co-field'>
						<label htmlFor='wl-parent-phone'>
							Phone <span className='req'>*</span>
						</label>
						<input
							id='wl-parent-phone'
							className='co-input'
							type='tel'
							required
							autoComplete='tel'
							value={form.parentPhone}
							onChange={update('parentPhone')}
						/>
					</div>
					<div className='co-field'>
						<label htmlFor='wl-student-name'>
							Student name <span className='req'>*</span>
						</label>
						<input
							id='wl-student-name'
							className='co-input'
							type='text'
							required
							value={form.studentName}
							onChange={update('studentName')}
						/>
					</div>
					<div className='co-field'>
						<label htmlFor='wl-student-age'>
							Student age <span className='req'>*</span>
						</label>
						<input
							id='wl-student-age'
							className='co-input'
							type='number'
							min='3'
							max='19'
							required
							value={form.studentAge}
							onChange={update('studentAge')}
						/>
					</div>
					<div className='co-field full'>
						<label htmlFor='wl-note'>
							Anything we should know? <span className='optional'>(optional)</span>
						</label>
						<textarea
							id='wl-note'
							className='co-input'
							rows={2}
							value={form.note}
							onChange={update('note')}
						/>
					</div>
				</div>

				{error && (
					<Alert variant='danger' className='mt-3 mb-0'>
						{error}
					</Alert>
				)}

				<button
					type='submit'
					className='btn-cyv-teal co-pay'
					disabled={submitting}>
					{submitting ? (
						<>
							<Spinner animation='border' size='sm' className='me-2' />
							Joining waitlist…
						</>
					) : (
						'Join the waitlist'
					)}
				</button>
			</div>
		</form>
	);
};

export default WaitlistForm;
